"use client"

import { AuthFormHeading } from "./auth-form-heading"

import Link from "next/link"
import { useState } from "react"
import { CheckCircle2, Copy, Info, Loader2 } from "lucide-react"

import { Alert, AlertDescription, AlertTitle } from "@/shared/components/ui/alert"
import { Button } from "@/shared/components/ui/button"

import type { KayitDurum } from "../types/kayit-types"

interface KayitHazirlaniyorProps {
  durum: KayitDurum | null
  tenantCode: string | null
  kurulumUzadi: boolean
}

function TenantKodu({ tenantCode }: { tenantCode: string }) {
  const [kopyalandi, setKopyalandi] = useState(false)

  const kopyala = async () => {
    try {
      await navigator.clipboard.writeText(tenantCode)
      setKopyalandi(true)
      window.setTimeout(() => setKopyalandi(false), 2000)
    } catch {
      // Pano izni yoksa kullanıcı kodu elle seçebilir.
    }
  }

  return (
    <div className="flex items-center justify-center gap-2 rounded-lg border bg-muted/40 px-4 py-3">
      <span className="font-mono text-lg font-semibold tracking-wide">{tenantCode}</span>
      <Button type="button" size="icon" variant="ghost" onClick={kopyala} aria-label={kopyalandi ? "Müşteri kodu kopyalandı" : "Müşteri kodunu kopyala"}>
        {kopyalandi ? <CheckCircle2 className="text-primary" /> : <Copy />}
      </Button>
    </div>
  )
}

/**
 * §1 — doğrulama sonrası şema kurulumu ekranı ve AKTIF olduğunda giriş yönlendirmesi.
 *
 * Kurulum normalde şablon şemanın klonlanmasıdır (saniyeler). Şablon henüz kurulu
 * değilse (ilan sonrası ilk kayıt) backend klasik `create_schema + migrate_schemas`
 * yoluna düşer ve uzak veritabanında ~30 dakika sürebilir. Bu **kalıcı hata
 * değildir**; uzun sürede paniğe değil beklemeye yönlendiriyoruz.
 */
export function KayitHazirlaniyor({
  durum,
  tenantCode,
  kurulumUzadi,
}: KayitHazirlaniyorProps) {
  if (durum === "AKTIF") {
    return (
      <div className="space-y-6 text-center">
        <CheckCircle2 className="mx-auto size-12 text-primary" />
        <AuthFormHeading title="Hesabınız hazır">
            Giriş şifreniz e-posta adresinize gönderildi. Giriş ekranında istenen
            <strong> müşteri kodu </strong> aşağıdaki değerdir.
          </AuthFormHeading>

        {tenantCode && <TenantKodu tenantCode={tenantCode} />}

        <Button asChild className="w-full">
          <Link href={tenantCode ? `/login?tenant=${tenantCode}` : "/login"}>
            Giriş yap
          </Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6 text-center">
      <Loader2 className="mx-auto size-10 animate-spin text-primary" />
      <AuthFormHeading title="Hesabınız kuruluyor">
          Veritabanı kurulumunuz hazırlanıyor; birkaç dakika içinde hazır olacak. Bu
          sayfayı açık bırakabilirsiniz.
        </AuthFormHeading>

      {tenantCode && <TenantKodu tenantCode={tenantCode} />}

      {kurulumUzadi && (
        <Alert className="text-left">
          <Info />
          <AlertTitle>Kurulum uzuyor</AlertTitle>
          <AlertDescription>
            <p>
              Bu bir hata değil: ilk kurulumlar yarım saati bulabiliyor. Hesabınız
              hazır olduğunda giriş bilgileriniz e-postayla gelir, bu sayfayı
              kapatabilirsiniz. Belirgin biçimde uzarsa müşteri kodunuzla destek
              ekibimize yazın.
            </p>
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}
